import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Chip,
  Alert,
  TextField,
  IconButton,
  Collapse,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Snackbar
} from '@mui/material';
import {
  Assignment,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Close as CloseIcon,
  Merge as MergeIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { AgentService } from '../../services/api';
import { API_CONFIG } from '../../config/apiConfig';
import TaskCreationProcessingModal from './TaskCreationProcessingModal';
import ReactMarkdown from 'react-markdown';

interface TaskSuggestion {
  title: string;
  description?: string;
  context?: string;
  suggestedAssignee?: string;
  suggestedPriority?: 'low' | 'medium' | 'high' | 'urgent';
  dueDate?: string;
  metadata?: any;
}

interface Teammate {
  id: string;
  name: string;
  type: string;
  isCustom: boolean;
}

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  metadata?: any;
  timestamp?: string;
}

interface TaskSuggestionsSplitViewProps {
  open: boolean;
  onClose: () => void;
  tasks: TaskSuggestion[];
  availableTeammates: Teammate[];
  conversationId: string;
  sourceAgent?: string;
  sourceMessage?: Message;
}

const TaskSuggestionsSplitView: React.FC<TaskSuggestionsSplitViewProps> = ({
  open,
  onClose,
  tasks,
  availableTeammates: initialTeammates,
  conversationId,
  sourceAgent,
  sourceMessage
}) => {
  const [selectedTasks, setSelectedTasks] = useState<Set<number>>(new Set());
  const [taskAssignments, setTaskAssignments] = useState<Record<number, string>>({});
  const [taskPriorities, setTaskPriorities] = useState<Record<number, string>>({});
  const [taskPrompts, setTaskPrompts] = useState<Record<number, string>>({});
  const [expandedPrompts, setExpandedPrompts] = useState<Set<number>>(new Set());
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [availableTeammates, setAvailableTeammates] = useState<Teammate[]>(initialTeammates || []);
  const [showProcessingModal, setShowProcessingModal] = useState(false);
  const [showCombineDialog, setShowCombineDialog] = useState(false);
  const [combinedTaskTitle, setCombinedTaskTitle] = useState('');
  const [combinedTaskDescription, setCombinedTaskDescription] = useState('');
  const [localTasks, setLocalTasks] = useState<TaskSuggestion[]>(tasks);

  // Update local tasks when prop changes
  useEffect(() => {
    setLocalTasks(tasks);
  }, [tasks]);

  // Fetch teammates if not provided
  useEffect(() => {
    const fetchTeammates = async () => {
      if (open && (!availableTeammates || availableTeammates.length === 0)) {
        try {
          const agentService = new AgentService();
          const agents = await agentService.getAgents();

          const formattedAgents = agents.map((agent: any) => ({
            id: agent.id,
            name: agent.custom_name || agent.name,
            type: agent.type,
            isCustom: agent.is_custom || false
          }));

          setAvailableTeammates(formattedAgents);
        } catch (err) {
          console.error('Failed to fetch teammates:', err);
        }
      }
    };

    fetchTeammates();
  }, [open, availableTeammates]);

  useEffect(() => {
    // Initialize task assignments, priorities, and prompts with suggested values
    console.log('TaskSuggestionsSplitView: Setting up defaults for tasks:', localTasks);
    console.log('Available teammates:', availableTeammates);

    const assignments: Record<number, string> = {};
    const priorities: Record<number, string> = {};
    const prompts: Record<number, string> = {};

    localTasks.forEach((task, index) => {
      // Only set values if they are properly provided by the backend
      if (task.suggestedAssignee) {
        const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(task.suggestedAssignee);

        if (isUUID) {
          assignments[index] = task.suggestedAssignee;
        } else {
          const teammate = availableTeammates.find(t =>
            t.name.toLowerCase() === task.suggestedAssignee?.toLowerCase()
          );
          if (teammate) {
            assignments[index] = teammate.id;
          }
        }
      }

      if (task.suggestedPriority) {
        priorities[index] = task.suggestedPriority;
      }

      // Initialize prompt with context if available, otherwise empty
      prompts[index] = task.context || '';
    });

    setTaskAssignments(assignments);
    setTaskPriorities(priorities);
    setTaskPrompts(prompts);
    // Select all tasks by default
    setSelectedTasks(new Set(localTasks.map((_, index) => index)));
  }, [localTasks, availableTeammates]);

  const handleToggleTask = (index: number) => {
    const newSelected = new Set(selectedTasks);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedTasks(newSelected);
  };

  const handleAssigneeChange = (index: number, assigneeId: string) => {
    setTaskAssignments({ ...taskAssignments, [index]: assigneeId });
  };

  const handlePriorityChange = (index: number, priority: string) => {
    setTaskPriorities({ ...taskPriorities, [index]: priority });
  };

  const handlePromptChange = (index: number, prompt: string) => {
    setTaskPrompts({ ...taskPrompts, [index]: prompt });
  };

  const togglePromptExpanded = (index: number) => {
    const newExpanded = new Set(expandedPrompts);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedPrompts(newExpanded);
  };

  const handleCombineTasks = () => {
    if (selectedTasks.size < 2) {
      setError('Please select at least 2 tasks to combine');
      return;
    }

    // Prepare combined task content
    const selectedTasksArray = Array.from(selectedTasks).sort((a, b) => a - b);
    const titles = selectedTasksArray.map(i => localTasks[i].title).join(', ');
    const contexts = selectedTasksArray
      .map(i => localTasks[i].context || localTasks[i].description || '')
      .filter(c => c.length > 0)
      .join('\n\n');

    setCombinedTaskTitle(titles.substring(0, 100) + (titles.length > 100 ? '...' : ''));
    setCombinedTaskDescription(contexts);
    setShowCombineDialog(true);
  };

  const confirmCombineTasks = () => {
    if (!combinedTaskTitle.trim()) {
      setError('Please provide a title for the combined task');
      return;
    }

    // Create a new combined task
    const selectedTasksArray = Array.from(selectedTasks).sort((a, b) => a - b);
    const firstTaskIndex = selectedTasksArray[0];

    // Use the first selected task as the base and update it
    const combinedTask: TaskSuggestion = {
      title: combinedTaskTitle,
      description: combinedTaskDescription,
      context: selectedTasksArray.map(i => `- ${localTasks[i].title}`).join('\n'),
      suggestedAssignee: localTasks[firstTaskIndex].suggestedAssignee,
      suggestedPriority: localTasks[firstTaskIndex].suggestedPriority,
      metadata: { combined: true, originalCount: selectedTasks.size }
    };

    // Remove selected tasks and add combined task
    const newTasks = localTasks.filter((_, index) => !selectedTasks.has(index));
    newTasks.unshift(combinedTask); // Add at beginning

    // Update local state
    setLocalTasks(newTasks);

    // Clear selection and close dialog
    setSelectedTasks(new Set([0])); // Select the new combined task
    setShowCombineDialog(false);
    setCombinedTaskTitle('');
    setCombinedTaskDescription('');
    setSuccess(`Combined ${selectedTasksArray.length} tasks into one`);
  };

  const handleCreateTasks = async () => {
    setCreating(true);
    setError(null);
    setShowProcessingModal(true);

    try {
      // First, generate comprehensive prompts for each task with user instructions
      const tasksToCreate = await Promise.all(
        Array.from(selectedTasks).map(async (index) => {
          const task = localTasks[index];
          // Use task-provided due date or default to tomorrow
          const dueDate = task.dueDate ? new Date(task.dueDate) : new Date();
          if (!task.dueDate) {
            dueDate.setDate(dueDate.getDate() + 1); // Default to tomorrow if no due date provided
          }

          // Get user's instructions
          const userInstructions = taskPrompts[index] || '';

          // Generate comprehensive prompt by calling the backend
          let finalPrompt = '';
          try {
            const response = await axios.post(
              `${API_CONFIG.AGENT_SERVICE_URL}/api/v1/tools/generate-prompt`,
              {
                taskTitle: task.title,
                userInstructions,
                conversationId,
                assignedTeammate: availableTeammates.find(t => t.id === taskAssignments[index])?.name || 'teammate'
              },
              {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('authToken') || localStorage.getItem('token')}`,
                  'Content-Type': 'application/json'
                }
              }
            );
            finalPrompt = response.data.prompt || `Complete the task: "${task.title}". ${userInstructions}`;
          } catch (err) {
            // Fallback to simple prompt if generation fails
            finalPrompt = `Complete the task: "${task.title}". ${userInstructions}`;
          }

          // Truncate description to meet the 5000 character limit
          const truncatedDescription = finalPrompt.length > 5000
            ? finalPrompt.substring(0, 4997) + '...'
            : finalPrompt;

          return {
            title: task.title,
            description: truncatedDescription,
            status: 'pending',  // Explicitly set status to pending
            priority: taskPriorities[index], // Only use if backend provided it
            assigned_user_agent_id: taskAssignments[index], // Only use if backend provided it
            due_date: task.dueDate || dueDate.toISOString(), // Use backend due date if provided
            source: 'task_detection',
            source_reference: conversationId,
            metadata: {
              created_from: 'task_detection',
              source_agent: sourceAgent || 'unknown',
              conversation_id: conversationId,
              task_prompt: finalPrompt,  // Store full prompt in metadata
              user_instructions: userInstructions
            }
          };
        })
      );

      // Create tasks via TODO service through API
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');

      const promises = tasksToCreate.map(task =>
        axios.post(
          `${API_CONFIG.TODO_SERVICE_URL}/api/v1/tasks`,
          task,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        ).catch(err => {
          console.error('Task creation error details:', err.response?.data);
          console.error('Task being sent:', task);
          throw err;
        })
      );

      await Promise.all(promises);

      setSuccess(`${selectedTasks.size} tasks created successfully`);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error creating tasks:', error);
      setError('Failed to create tasks');
    } finally {
      setCreating(false);
      setShowProcessingModal(false);
    }
  };


  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        style: {
          width: '90vw',
          height: '85vh',
          maxWidth: '1600px'
        }
      }}
    >
      <DialogTitle>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Assignment color="primary" />
            <Typography variant="h6">Task Detection from Agent Response</Typography>
            {sourceAgent && (
              <Chip label={sourceAgent} size="small" color="primary" variant="outlined" />
            )}
          </div>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent style={{ padding: 0, height: 'calc(100% - 120px)' }}>
        <div style={{ display: 'flex', height: '100%' }}>
          {/* Left Pane: Agent Response */}
          <div
            style={{
              width: '50%',
              borderRight: '1px solid rgba(0,0,0,0.12)',
              overflow: 'auto',
              padding: 24,
              backgroundColor: '#fafafa'
            }}
          >
            <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
              Agent Response
            </Typography>
            <Paper
              variant="outlined"
              style={{
                padding: 16,
                backgroundColor: '#fff',
                maxHeight: 'calc(100% - 40px)',
                overflow: 'auto'
              }}
            >
              {sourceMessage ? (
                <div>
                  <Typography variant="caption" color="text.secondary">
                    {sourceMessage.timestamp ? new Date(sourceMessage.timestamp).toLocaleString() : ''}
                  </Typography>
                  <div style={{ marginTop: 8 }}>
                    <ReactMarkdown>
                      {sourceMessage.content}
                    </ReactMarkdown>
                  </div>
                </div>
              ) : (
                <Typography color="text.secondary">
                  Original message content not available
                </Typography>
              )}
            </Paper>
          </div>

          {/* Right Pane: Task Panel */}
          <div
            style={{
              width: '50%',
              overflow: 'auto',
              padding: 24,
              backgroundColor: '#fff'
            }}
          >
            <Stack spacing={2}>
              <Alert severity="info">
                {localTasks.length} potential task{localTasks.length !== 1 ? 's' : ''} detected.
                Select the ones you'd like to create and assign to your teammates.
              </Alert>

              {localTasks.length === 0 ? (
                <Alert severity="warning">
                  No tasks detected in the agent response. Try being more explicit with task indicators like "TODO:", "Task:", or action verbs.
                </Alert>
              ) : (
                <List style={{ maxHeight: 'calc(100vh - 350px)', overflow: 'auto' }}>
                  {localTasks.map((task, index) => {
                    return (
                      <ListItem
                        key={index}
                        style={{
                          border: selectedTasks.has(index) ? '1px solid #1976d2' : '1px solid rgba(0,0,0,0.12)',
                          borderRadius: 4,
                          marginBottom: 8,
                          padding: 16,
                          backgroundColor: selectedTasks.has(index) ? 'rgba(0,0,0,0.04)' : '#fff'
                        }}
                      >
                        <Checkbox
                          checked={selectedTasks.has(index)}
                          onChange={() => handleToggleTask(index)}
                          color="primary"
                          style={{ marginRight: 16 }}
                        />

                        <div style={{ flex: 1 }}>
                          <ListItemText
                            primary={
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Typography variant="subtitle2">{task.title}</Typography>
                                {task.metadata?.type && (
                                  <Chip
                                    label={task.metadata.type}
                                    size="small"
                                    variant="outlined"
                                    style={{ fontSize: '0.7rem' }}
                                  />
                                )}
                              </div>
                            }
                          />

                          {/* Editable Prompt Section */}
                          <div style={{ marginTop: 8, marginBottom: 16 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <Typography variant="body2" color="text.secondary" fontWeight="medium">
                                Task Details
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => togglePromptExpanded(index)}
                                style={{ marginLeft: 'auto' }}
                              >
                                {expandedPrompts.has(index) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </IconButton>
                            </div>
                            <Collapse in={expandedPrompts.has(index)} timeout="auto">
                              <TextField
                                fullWidth
                                multiline
                                rows={3}
                                value={taskPrompts[index] || ''}
                                onChange={(e) => handlePromptChange(index, e.target.value)}
                                variant="outlined"
                                size="small"
                                disabled={!selectedTasks.has(index)}
                                style={{ marginTop: 8 }}
                                placeholder="Add any specific details or instructions for this task..."
                              />
                            </Collapse>
                          </div>

                          {/* Task Controls */}
                          <Stack direction="row" spacing={1} mt={2}>
                            <FormControl size="small" style={{ minWidth: 150 }}>
                              <InputLabel>Assignee</InputLabel>
                              <Select
                                value={taskAssignments[index] || ''}
                                onChange={(e) => handleAssigneeChange(index, e.target.value)}
                                label="Assignee"
                                disabled={!selectedTasks.has(index)}
                              >
                                <MenuItem value="">
                                  <em>None</em>
                                </MenuItem>
                                {availableTeammates.map(teammate => (
                                  <MenuItem key={teammate.id} value={teammate.id}>
                                    {teammate.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>

                            <FormControl size="small" style={{ minWidth: 100 }}>
                              <InputLabel>Priority</InputLabel>
                              <Select
                                value={taskPriorities[index] || 'medium'}
                                onChange={(e) => handlePriorityChange(index, e.target.value)}
                                label="Priority"
                                disabled={!selectedTasks.has(index)}
                              >
                                <MenuItem value="low">Low</MenuItem>
                                <MenuItem value="medium">Medium</MenuItem>
                                <MenuItem value="high">High</MenuItem>
                                <MenuItem value="urgent">Urgent</MenuItem>
                              </Select>
                            </FormControl>
                          </Stack>
                        </div>
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </Stack>
          </div>
        </div>
      </DialogContent>

      <DialogActions style={{ paddingLeft: 24, paddingRight: 24, paddingTop: 16, paddingBottom: 16 }}>
        <Button onClick={onClose} disabled={creating}>
          Cancel
        </Button>
        {selectedTasks.size >= 2 && (
          <Button
            onClick={handleCombineTasks}
            disabled={creating}
            color="secondary"
            variant="outlined"
            startIcon={<MergeIcon />}
          >
            Combine Selected ({selectedTasks.size})
          </Button>
        )}
        <Button
          onClick={handleCreateTasks}
          variant="contained"
          disabled={selectedTasks.size === 0 || creating}
          startIcon={creating ? <CircularProgress size={20} /> : <Assignment />}
        >
          Create {selectedTasks.size} Task{selectedTasks.size !== 1 ? 's' : ''}
        </Button>
      </DialogActions>

      {/* Task Creation Processing Modal */}
      <TaskCreationProcessingModal
        open={showProcessingModal}
        onClose={() => setShowProcessingModal(false)}
      />

      {/* Combine Tasks Dialog */}
      <Dialog
        open={showCombineDialog}
        onClose={() => setShowCombineDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Combine Selected Tasks</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" style={{ marginBottom: 16 }}>
            Combining {selectedTasks.size} tasks into a single task. You can edit the title and description below.
          </Typography>
          <TextField
            fullWidth
            label="Combined Task Title"
            value={combinedTaskTitle}
            onChange={(e) => setCombinedTaskTitle(e.target.value)}
            variant="outlined"
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Combined Task Description"
            value={combinedTaskDescription}
            onChange={(e) => setCombinedTaskDescription(e.target.value)}
            variant="outlined"
            margin="normal"
            multiline
            rows={4}
            helperText="The original task titles will be preserved as context"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCombineDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={confirmCombineTasks}
            variant="contained"
            color="primary"
          >
            Combine Tasks
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error/Success Snackbars */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default TaskSuggestionsSplitView;